import { useEffect, useState } from "react";
import SimpleModal from "app/components/Modals/SimpleModal";
import { Dropdown } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import SendEmailConfirmation from "./SendEmailConfirmation";
import OrganizationEmailConfig from "./OrganizationEmailConfig";
import TeamEmailConfig from "./TeamEmailConfig";
import UserEmailConfig from "./UserEmailConfig";
import DepartmentEmailConfig from "./DepartmentEmailConfig";
import AdditionalFieldEmailConfig from "./AdditionalFieldEmailConfig";
import ManagerEmailConfig from "./ManagerEmailConfig";
import RecipientsListScreen from "./RecipientsListScreen";
import {
  getAdditionalFields,
  getEmailRecipientsPreview,
  selectAdditionalFields,
  selectEmailRecipientsPreview,
  selectGetEmailRecipientsPreviewStatus,
} from "../slice";
import {
  AdditionalFieldSelection,
  EmailManagementValidEmailType,
  ManagerScope,
  SendEmailOptions,
  SendEmailPayloadOptions,
  SendToType,
} from "../types";

interface Props {
  isOpen: boolean;
  title: string;
  titleDescription: string;
  closeModal: () => void;
  emailType: EmailManagementValidEmailType;
  userConfig?: {
    checkIfUserHasError: (userAccountId: number | string) => boolean;
    userErrorMessage: string;
  };
  onSave?: (
    type: SendToType,
    ids: number[],
    options: SendEmailPayloadOptions,
    excludedUserAccountIds: number[]
  ) => Promise<void> | void;
  isLoading?: boolean;
  getWarningBanner?: (sendToType: string) => JSX.Element | null;
  confirmationDescriptionText?: string;
  sendToTypeDefault?: SendToType;
}

const SendEmailModal = ({
  title,
  isOpen,
  closeModal,
  titleDescription,
  emailType,
  onSave,
  userConfig,
  isLoading,
  getWarningBanner,
  confirmationDescriptionText,
  sendToTypeDefault,
}: Props) => {
  const dispatch = useAppDispatch();
  const emailRecipientsPreview = useAppSelector(selectEmailRecipientsPreview);
  const getEmailRecipientsPreviewStatus = useAppSelector(
    selectGetEmailRecipientsPreviewStatus
  );
  const [sendToType, setSendToType] = useState<SendToType>("teams");
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false);
  const [showRecipientsListScreen, setShowRecipientsListScreen] =
    useState(false);
  const [sendEmailOptions, setSendEmailOptions] = useState<SendEmailOptions>(
    {}
  );
  const [ids, setIds] = useState<number[]>([]);
  const [excludedUserAccountIds, setExcludedUserAccountIds] = useState<
    number[]
  >([]);
  const [additionalFieldSelection, setAdditionalFieldSelection] =
    useState<AdditionalFieldSelection | null>(null);
  const [managerScope, setManagerScope] = useState<ManagerScope | null>(null);
  const additionalFields = useAppSelector(selectAdditionalFields);

  const buildPayloadOptions = (
    options: SendEmailOptions,
    fieldSelection: AdditionalFieldSelection | null,
    scope: ManagerScope | null
  ): SendEmailPayloadOptions => ({
    ...options,
    ...(fieldSelection ?? {}),
    ...(scope ? { managerScope: scope } : {}),
  });

  useEffect(() => {
    setSendToType(sendToTypeDefault ?? "teams");
  }, [sendToTypeDefault]);

  useEffect(() => {
    dispatch(getAdditionalFields());
  }, [dispatch]);

  const onDropdownSelect = (e: string | null) => {
    setSendToType((e as SendToType) ?? "teams");
    setIds([]);
    setAdditionalFieldSelection(null);
    setManagerScope(null);
  };

  const closeModalWrapper = () => {
    setSendToType(sendToTypeDefault ?? "teams");
    setIds([]);
    closeModal();
    setShowConfirmationScreen(false);
    setShowRecipientsListScreen(false);
    setSendEmailOptions({});
    setExcludedUserAccountIds([]);
    setAdditionalFieldSelection(null);
    setManagerScope(null);
  };

  const getTypeDropdownSelected = () => {
    if (sendToType === "organization") {
      return "Entire Organization";
    }
    if (sendToType === "additionalField") {
      return "Additional Field";
    }

    const typeUpper = sendToType.charAt(0).toUpperCase() + sendToType.slice(1);
    return `Select ${typeUpper}`;
  };

  const getTypeDropdown = () => {
    return (
      <Dropdown className="full-width-dropdown" onSelect={onDropdownSelect}>
        <Dropdown.Toggle variant="light" id="dropdown-basic">
          {getTypeDropdownSelected()}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item eventKey="users">Users</Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="teams">Teams</Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="departments">Departments</Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="managers">Managers</Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="organization">Organization</Dropdown.Item>
          {!!additionalFields?.length && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="additionalField">
                Additional Field
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const onSaveEmailConfig = (
    selectedIds: number[],
    selectedOptionValues?: SendEmailOptions,
    fieldSelection: AdditionalFieldSelection | null = null,
    scope: ManagerScope | null = null
  ) => {
    const resolvedOptions = selectedOptionValues ?? {};
    setIds(selectedIds);
    setShowConfirmationScreen(true);
    setSendEmailOptions(resolvedOptions);
    setAdditionalFieldSelection(fieldSelection);
    setManagerScope(scope);
    // A new selection resolves a new recipient list, so exclusions picked against
    // the previous one no longer refer to anything the admin reviewed.
    setExcludedUserAccountIds([]);
    dispatch(
      getEmailRecipientsPreview({
        emailType,
        sendToType,
        ids: selectedIds,
        options: buildPayloadOptions(resolvedOptions, fieldSelection, scope),
      })
    );
  };

  const onBackClick = () => {
    if (showRecipientsListScreen) {
      return setShowRecipientsListScreen(false);
    }
    if (showConfirmationScreen) {
      return setShowConfirmationScreen(false);
    }
    closeModalWrapper();
  };

  const onSaveRecipientExclusions = (userAccountIds: number[]) => {
    setExcludedUserAccountIds(userAccountIds);
    setShowRecipientsListScreen(false);
  };

  const getAdditionalSettings = () => {
    switch (sendToType) {
      case "organization":
        return (
          <OrganizationEmailConfig
            onBack={onBackClick}
            onSave={(selectedOptions) => onSaveEmailConfig([], selectedOptions)}
            sendEmailOptions={sendEmailOptions}
            getWarningBanner={getWarningBanner}
          />
        );
      case "teams":
        return (
          <TeamEmailConfig
            onBack={onBackClick}
            onSave={onSaveEmailConfig}
            preselectedUserIds={ids}
            sendEmailOptions={sendEmailOptions}
            getWarningBanner={getWarningBanner}
          />
        );
      case "users":
        return (
          <UserEmailConfig
            onBack={onBackClick}
            onSave={onSaveEmailConfig}
            preselectedUserIds={ids}
            checkIfUserHasError={userConfig?.checkIfUserHasError}
            errorMessage={userConfig?.userErrorMessage}
          />
        );
      case "departments":
        return (
          <DepartmentEmailConfig
            onBack={onBackClick}
            onSave={onSaveEmailConfig}
            preselectedUserIds={ids}
            sendEmailOptions={sendEmailOptions}
            getWarningBanner={getWarningBanner}
          />
        );
      case "managers":
        return (
          <ManagerEmailConfig
            onBack={onBackClick}
            onSave={(selectedIds, scope) =>
              onSaveEmailConfig(selectedIds, undefined, null, scope)
            }
            preselectedUserIds={ids}
            managerScope={managerScope ?? undefined}
            getWarningBanner={getWarningBanner}
          />
        );
      case "additionalField":
        return (
          <AdditionalFieldEmailConfig
            onBack={onBackClick}
            onSave={(fieldSelection) =>
              onSaveEmailConfig([], undefined, fieldSelection)
            }
          />
        );
      default:
        return null;
    }
  };

  const getModalContent = () => {
    if (showRecipientsListScreen) {
      return (
        <RecipientsListScreen
          preview={emailRecipientsPreview}
          isLoading={getEmailRecipientsPreviewStatus === "loading"}
          hasError={getEmailRecipientsPreviewStatus === "failed"}
          excludedUserAccountIds={excludedUserAccountIds}
          onSave={onSaveRecipientExclusions}
          onCancel={onBackClick}
        />
      );
    }

    if (showConfirmationScreen) {
      return (
        <SendEmailConfirmation
          sendToType={sendToType}
          ids={ids}
          onSave={async () => {
            await onSave?.(
              sendToType,
              ids,
              buildPayloadOptions(
                sendEmailOptions,
                additionalFieldSelection,
                managerScope
              ),
              excludedUserAccountIds
            );
            closeModalWrapper();
          }}
          onBack={onBackClick}
          onViewRecipients={() => setShowRecipientsListScreen(true)}
          recipientsPreview={emailRecipientsPreview}
          isRecipientsPreviewLoading={
            getEmailRecipientsPreviewStatus === "loading"
          }
          hasRecipientsPreviewError={
            getEmailRecipientsPreviewStatus === "failed"
          }
          excludedUserAccountIds={excludedUserAccountIds}
          additionalFieldSelection={additionalFieldSelection}
          managerScope={managerScope}
          sendEmailOptions={sendEmailOptions}
          isLoading={isLoading}
          getWarningBanner={getWarningBanner}
          confirmationDescriptionText={confirmationDescriptionText}
          emailType={emailType}
        />
      );
    }

    return (
      <>
        {getTypeDropdown()}
        {getAdditionalSettings()}
      </>
    );
  };

  return (
    <SimpleModal show={isOpen} onHide={closeModalWrapper} title={title}>
      {showConfirmationScreen || showRecipientsListScreen ? null : (
        <div>
          <p>{titleDescription}</p>
        </div>
      )}
      {getModalContent()}
    </SimpleModal>
  );
};

export default SendEmailModal;
